// @flow
import React, { useState, useEffect } from 'react';
import ApolloClient from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import styled, { ThemeProvider, css } from 'styled-components';
import DevStyle, { ProdStyle } from './styles/GlobalStyle';
import themes from './theme';
import { screen } from './lib/utils';

import logo from './artglass-logo.svg';
import Button, { ButtonGroup } from './components/Button';
import Form, { MapForm } from './Form';

const { PUBLIC_URL, NODE_ENV } = process.env;

const client = new ApolloClient({
  uri: 'https://groglass.com/graphql',
});

const StyledApp = styled.div`
  /* min-height: 100vh; */
  display: flex;
  flex-direction: column;
  font-family: ${({ theme }) => theme.fonts.default};
  background-color: ${({ theme }) => theme.colors.bg};

  /* Global styles */
  *,
  ::before,
  ::after {
    box-sizing: border-box;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: inherit;
    color: ${({ theme }) => theme.colors.acc1};
  }

  h1 {
    font-size: 33px;
    line-height: 40px;

    & + h2 {
      margin-top: 0.25rem;
    }

    ${screen.atLeast.tablet`
      font-size: 50px;
      line-height: 60px;
    `}
  }

  h2 {
    font-weight: normal;
    font-size: 20px;
    letter-spacing: 0.03em;
    margin-bottom: 1.5em;

    ${screen.atLeast.tablet`
      font-size: 30px;
    `}
  }

  input,
  textarea,
  button {
    font-family: ${({ theme }) => theme.fonts.default};
    font-weight: bold;
  }

  p,
  a {
    letter-spacing: 0.05em;
  }

  p {
    font-size: 1.125rem;

    ${screen.atLeast.tablet`
      font-size: 1.25rem;
    `}
  }

  a {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Main = styled.main`
  display: flex;
  width: 100%;

  ${screen.smallerThan.desktop`
    flex-direction: column;
  `};
`;

const FeaturedImage = styled.div`
  width: 100%;
  padding-top: 100%;
  background-image: url('${PUBLIC_URL}/images/bg-mobile-${({ imageId }) =>
  imageId}.jpg');
  background-size: cover;
  background-position: center;

  ${screen.atLeast.tablet`
    padding-top: 63%;
    background-image: url('${PUBLIC_URL}/images/bg-tablet-${({ imageId }) =>
    imageId}.jpg');
  `}

  ${screen.atLeast.desktop`
    flex: 0 1 50%;
    padding-top: 50%;
    background-image: url('${PUBLIC_URL}/images/bg-desktop-${({ imageId }) =>
    imageId}.jpg');
  `}
`;

const Content = styled.article`
  padding: 0 21px;

  h1 + p {
    margin: 0 0 50px;

    ${screen.atLeast.desktop`
      margin: 0 0 60px;
    `}
  }

  ${screen.atLeast.tablet`
    padding: 0 15%;
  `}

  ${screen.atLeast.desktop`
    padding: 0 141px 0 94px;
    flex: 0 1 50%;
  `}

  .artglass-logo {
    margin: 2rem 0 0;

    ${screen.atLeast.tablet`
      margin: 3rem 0 0;
    `}

    ${screen.atLeast.desktop`
      margin: 1rem 0 3rem;
    `}

    & + h1 {
      margin-top: 1rem;
    }
  }

  /* Vertically centered */
  ${({ verticallyCentered }) =>
    verticallyCentered &&
    css`
      ${screen.atLeast.desktop`
        display: flex;
        flex-direction: column;
        justify-content: center;
      `}
    `}

  /* First slide */
  ${({ slide }) =>
    slide === '1' &&
    css`
      h1,
      ${Button} {
        margin: 50px 0 0;
        ${screen.atLeast.tablet`margin: 70px 0 0;`}
      }

      ${Button} {
        align-self: flex-start;
      }

      p {
        margin: 50px 0;
        ${screen.atLeast.tablet`margin: 70px 0;`}
      }
    `}
`;

const ContentGroup = styled.div`
  ${screen.atLeast.desktop`
    display: flex;
    flex-direction: column;
    flex: 0 1 50%;

    ${Content} {
      flex-grow: 1;
    }
  `}
`;

const Voucher = styled.aside`
  padding: .25rem 21px;

  h1 {
    font-weight: normal;
    letter-spacing: 0.05em;
    margin: 0;
  }

  p {
    margin-top: 0;
    margin-bottom: .5em;

    &:first-of-type {
      margin-top: .5em;
      margin-bottom: 0;
    }
  }

  ${screen.atLeast.tablet`
    padding: 1em 15%;

    p {
      font-size: 30px;
    }
  `}

  ${screen.atLeast.desktop`
    padding: 2rem 141px 2rem 94px;
  `}

  background-color: ${({ theme }) => theme.colors.lightGray};
`;

// const Footer = styled.footer`
//   flex: 1;
//   background-color: ${({ theme }) => theme.colors.black};
//   color: ${({ theme }) => theme.colors.white};
// `;

function App() {
  const [slide, setSlide] = useState(1);
  const [submissionId, setSubmissionId] = useState(0);
  const [promoCode, setPromoCode] = useState();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slide]);

  return (
    <ThemeProvider theme={themes.default}>
      {NODE_ENV === 'development' ? <DevStyle /> : <ProdStyle />}
      <ApolloProvider client={client}>
        <StyledApp>
          <Main>
            <FeaturedImage imageId={slide > 3 ? 3 : slide} />
            {slide === 1 && (
              <Content slide="1" verticallyCentered>
                <h1>
                  A limited time offer:
                  <br />
                  get an exlusive discount
                  <br />
                  on your next order.*
                </h1>
                <Button type="button" onClick={() => setSlide(slide + 1)}>
                  Get Discount →
                </Button>
                <p>* Valid from 27/01/2020 - 31/04/2020</p>
              </Content>
            )}
            {slide === 2 && (
              <Content verticallyCentered>
                <h1>
                  Please provide the e-mail address of your framing shop to
                  receive the discount code!
                </h1>
                <Form
                  afterSubmit={(data) => {
                    const {
                      submission: { id, promoCode: newPromoCode },
                    } = data;
                    setPromoCode(newPromoCode);
                    setSubmissionId(id);
                    setSlide(slide + 1);
                  }}
                />
              </Content>
            )}
            {slide === 3 && (
              <ContentGroup>
                <Voucher>
                  <p>This is your code:</p>
                  <h1>{promoCode}</h1>
                  <p>We have sent it to your email address</p>
                </Voucher>
                <Content verticallyCentered>
                  <h2
                    style={{
                      fontWeight: 'bold',
                      marginBottom: 0,
                      padding: '1em 0',
                    }}
                  >
                    To join the Artglass Framers map,
                    <br />
                    please provide
                  </h2>
                  <MapForm id={submissionId} afterSubmit={() => setSlide(4)} />
                </Content>
              </ContentGroup>
            )}
            {slide === 4 && (
              <Content verticallyCentered>
                <img
                  src={logo}
                  alt="Artglass logo"
                  className="artglass-logo"
                  style={{
                    width: 120,
                    height: 150,
                  }}
                />
                <h1 style={{ marginBottom: 0 }}>Thank you</h1>
                <h2>for framing with Artglass.</h2>
              </Content>
            )}
          </Main>
        </StyledApp>
      </ApolloProvider>
    </ThemeProvider>
  );
}

export default App;
